import * as Styled from "./EventOpsConsentModal.styles";

import { Box, Link, Stack, Typography, useTheme } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";

import BaseModal from "../BaseModal/BaseModal";
import { Checkbox } from "../../components";

type EventOpsConsentModalProps = {
  open: any;
  onClose: any;
  onConfirm: any;
};

type EventOpsConsentForm = {
  opsFormSubmitted: boolean;
};

const EventOpsConsentModal = ({
  open,
  onClose,
  onConfirm,
}: EventOpsConsentModalProps) => {
  const formMethods = useForm({
    defaultValues: {
      opsFormSubmitted: false,
    },
    mode: "onChange",
  });
  const theme = useTheme();

  const isFormSubmitted = formMethods.watch("opsFormSubmitted");

  const handleClose = () => {
    formMethods.reset(); // Reset the form state when the modal closes
    onClose();
  };

  const onSubmit = (data: EventOpsConsentForm) => {
    if (data.opsFormSubmitted) {
      handleClose();
      onConfirm();
    }
  };

  return (
    <BaseModal open={open} onClose={handleClose} showCloseButton={false}>
      <Box style={{ textAlign: "center" }}>
        <Styled.ModalTitleContainer>
          <Typography variant="h2">Before publishing your event</Typography>
        </Styled.ModalTitleContainer>
        <Typography
          variant="body2"
          style={{
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
          }}
          align="left"
        >
          Publishing this event in Gather will make it visible to everyone in
          the market. Before proceeding, please confirm you have submitted the{" "}
          <Link
            href="https://twodegrees1.sharepoint.com/sites/Market-NewEngland/SitePages/Ops-Support-Requests.aspx"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ops Event Request Form
          </Link>
          {` and have received approval from Events/Ops.`}
        </Typography>
        <Typography
          variant="body2"
          style={{
            marginTop: theme.spacing(2),
            marginBottom: theme.spacing(2),
          }}
          align="left"
        >
          {`If you haven’t heard back or are unsure, please email `}
          <Link href="mailto:NewEnglandTeamOperations@slalom.com">
            NewEnglandTeamOperations@slalom.com
          </Link>
          {" to confirm its status."}
        </Typography>
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              mb={2}
            >
              <Checkbox
                name="opsFormSubmitted"
                control={formMethods.control}
                required
                defaultChecked={false}
              />
              <Typography variant="body2" ml={1} align="left">
                I confirm that I have submitted the{" "}
                <Link
                  href="https://twodegrees1.sharepoint.com/sites/Market-NewEngland/SitePages/Ops-Support-Requests.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ops Event Request Form
                </Link>
                {` and have received approval from Events/Ops. I understand that
                this event will be visible to the entire market upon
                publication.`}
              </Typography>
            </Box>
            <Stack
              direction={{ mobile: "column", tablet: "row", desktop: "row" }}
              spacing={2}
              width="100%"
            >
              <Styled.ConfirmButton
                variant="contained"
                fullWidth
                size="large"
                type="submit"
                disabled={!isFormSubmitted}
              >
                All set, let's go!
              </Styled.ConfirmButton>
              <Styled.ConfirmButton
                variant="outlined"
                fullWidth
                size="large"
                onClick={handleClose}
              >
                Go Back
              </Styled.ConfirmButton>
            </Stack>
          </form>
        </FormProvider>
      </Box>
    </BaseModal>
  );
};

export default EventOpsConsentModal;
